// Shared layer types. Originally compositor_v0_schema v0.1 §4.
// Extracted into @gooddesign/anim-schema (Marionette kickoff, 2026-06-06).
//
// The `part` layer type + PartVariant are Marionette's addition (the fork work,
// §1 of the kickoff). They live in the shared package because the Layer union
// and LayerType discriminant have one home here; the Compositor never creates a
// part layer, so its behavior is unchanged.

export type LayerType =
  | 'text' | 'shape' | 'media' | 'figure' | 'aux_text' | 'part' | 'collage'
  // The filmed camera, AS A LAYER (spec §5d, Order 3.999). Matt, 2026-08-08:
  // "It integrates the camera layer into the final frame." Before this the camera
  // existed only as Composition.filler_background — a full-frame, inset:0,
  // compose-only backdrop pinned behind everything — so a composition could not
  // say WHERE the camera sits, and the `inset` export (material behind the camera
  // as well as in front) had nowhere to stand.
  | 'camera';
export type EstRole = 'bg' | 'ink' | 'complement' | 'contrast' | 'custom';

export type BlendMode =
  | 'normal' | 'multiply' | 'screen' | 'overlay'
  | 'darken' | 'lighten' | 'color-dodge' | 'color-burn'
  | 'hard-light' | 'soft-light' | 'difference' | 'exclusion'
  | 'hue' | 'saturation' | 'color' | 'luminosity';

export const BLEND_MODES: BlendMode[] = [
  'normal','multiply','screen','overlay','darken','lighten','color-dodge','color-burn',
  'hard-light','soft-light','difference','exclusion','hue','saturation','color','luminosity',
];

export type ShapePrimitive = 'rectangle';
export type MediaType = 'image' | 'video';
export type FigureType = 'still' | 'sequence';
export type Fit = 'cover' | 'contain' | 'fill';
export type TextAlign = 'left' | 'center' | 'right' | 'justify';

export interface Container {
  x: number; y: number; width: number; height: number;
  rotation: number; opacity: number;
}

// --- Phase 9 alpha / masking (compositor_spec §14.2) ------------------------
// The per-layer alpha record. Stored per layer so key color and params never
// collide across layers (NK routinely keys two different colors in one comp).
export type AlphaSource = 'none' | 'key-color' | 'segmentation' | 'outline-matte';
export type AlphaOrder = 'key-then-scale' | 'upscale-then-key';
export type AlphaPreset = 'loose-key' | 'sharp-matte' | null;
export type SegmentationModel = 'u2net' | 'u2netp' | 'isnet-general';

export interface AlphaKey {
  hex: string;          // the key color, PER LAYER
  tolerance: number;    // 0..1; how far from hex still keys out
  choke: number;        // px; erodes the matte edge inward (negative = expand)
  despill: number;      // 0..1; subtracts key-color spill from kept pixels
  edge_softness: number; // px feather on the matte edge; 0 = hard
}

export interface AlphaSegmentation {
  model: SegmentationModel; // default 'u2net'
  threshold: number;        // 0..1 cutoff on the model's soft mask; default 0.5
  feather: number;          // px feather on the resulting matte; default 0
}

export interface Alpha {
  source: AlphaSource;
  key: AlphaKey | null;                 // used when source === 'key-color'
  segmentation: AlphaSegmentation | null; // used when source === 'segmentation'
  matte_url: string | null;             // used when source === 'outline-matte'
  order: AlphaOrder;                    // ingest order for stills
  baked_url: string | null;            // ingest output (stills); null for video / 'none'
  baked_at: string | null;             // ISO datetime of last bake; null when stale
  preset: AlphaPreset;                 // which preset seeded these controls; null once hand-tuned
}

export interface LayerBase {
  id: string;
  type: LayerType;
  name: string;
  // Excluded from paint AND from export. Optional, so every composition
  // persisted before this round-trips unchanged (absent = visible).
  //
  // This is Matt's gate, not a convenience (spec §5d
  // `deliver.matt-picks-the-camera-layers`): "My job would be to come and
  // determine which of the camera layers to be used and just hide the other ones
  // inside that shot." A shot carries every camera angle it might use and he
  // hides all but the one. It is NOT opacity 0 — that still exports, as
  // transparent pixels, and still counts as a visible layer when deciding which
  // export a composition wants.
  hidden?: boolean;
  est_role: EstRole;
  custom_color_hex: string | null;
  container: Container;
  blend_mode: BlendMode;
  texture_treatment: null;
  // Optional on the type so compositions persisted before Phase 9 round-trip
  // cleanly; consumers read it through getLayerAlpha() which fills the 'none'
  // default. New layers (factories) always set it explicitly.
  alpha?: Alpha;
}

export interface Glyph {
  char: string;
  variant_id: string | null;
  rel: Container; // {x,y,width,height,rotation,opacity}, x/y relative to parent glyph
}

export interface ContainerTheme {
  enabled: boolean;
  padding: { top: number; right: number; bottom: number; left: number };
  background_est_role: EstRole;
  background_custom_hex: string | null;
  background_texture_url: string | null;
}

export interface TextLayer extends LayerBase {
  type: 'text';
  string: string;
  font_id: string;
  layout_theme_id: string | null;
  glyphs: Glyph[];
  // Extra horizontal advance between glyphs, in glyph-space units (same scale as glyph.rel.width).
  // 0 = glyphs sit immediately adjacent (the default). Negative tightens (kerns in), positive opens up.
  letter_spacing: number;
  // Optional per-layer seed used for glyph variant selection. When set, overrides
  // the composition-level seed so a single text layer can be rerolled in isolation
  // without disturbing other layers' variant assignments.
  seed?: string | null;
  container_theme: ContainerTheme | null;
}

export interface ShapeLayer extends LayerBase {
  type: 'shape';
  shape: ShapePrimitive;
  corner_radius: number;
}

export interface MediaParamsImage {}
export interface MediaParamsVideo {
  start_offset_seconds: number;
  playback_rate: number;
  muted: true;
}
export interface MediaLayer extends LayerBase {
  type: 'media';
  media_type: MediaType;
  source_url: string;
  fit: Fit;
  media_params: MediaParamsImage | MediaParamsVideo;
}

export interface FigureSequenceParams {
  frame_rate: number;
  loop: boolean;
  start_offset_frames: number;
}
export interface FigureLayer extends LayerBase {
  type: 'figure';
  figure_type: FigureType;
  source_url: string;
  source_urls: string[] | null;
  sequence_params: FigureSequenceParams | null;
  fit: Fit;
}

// FIT TO BOX (Order 3.9995, spec §5e `type.fits-its-box`, `type.breaks-on-words`).
//
// Matt, 2026-08-08: *"No content should be past a certain left and right and top
// and bottom padding where, let's say, a word is being clipped off the frame. We
// have to fit to the box in that regard per line... if necessary, the words will
// break up at the end of the word right into the next word on the next row
// below it."*
//
// WHY THE RENDERER FITS AND NOT THE COMPOSER. Fitting needs to MEASURE text, and
// the type these layers are set in — Archivo — is fetched from Google Fonts by
// the browser at runtime. There is no font file on disk to measure server-side,
// so a composer-side fitter would mean vendoring font binaries and keeping them
// in step with a CDN by hand: drift that arrives on its own with nobody deciding
// anything. The browser already has the real font, and every consumer that
// PAINTS a composition is a browser (RenderMode, and the render worker driving
// it through puppeteer). So the composition carries INTENT and the renderer
// resolves it.
//
// `font_size` therefore means THE SIZE THE TEXT WANTS, and is a maximum. Absent
// `fit`, it is also exactly what gets set — which is what the `bleed` treatment
// wants, since a glyph bleeding off the frame edge is a deliberate tell and must
// not be shrunk to avoid it.
export interface AuxTextFit {
  /** The renderer will not go below this, in px. Sized off the frame rather than
   *  off `font_size` so it means the same thing in both aspects: below roughly
   *  2% of frame height, type is unreadable at any viewing distance and the
   *  honest answer is an error, not a smaller number. */
  min_font_size: number;
}

export interface AuxTextLayer extends LayerBase {
  type: 'aux_text';
  content: string;
  font_family: string;
  font_weight: number;
  /** The size the text WANTS. With `fit` set this is a maximum — see AuxTextFit. */
  font_size: number;
  /** Fit-to-box intent. Present on every treatment except `bleed`. */
  fit?: AuxTextFit | null;
  line_height: number;
  letter_spacing: number;
  text_align: TextAlign;
  // 0..1. Render slices content to floor(content.length * reveal_progress).
  // Default 1 = fully revealed. Keyframable via KeyframeLayerValues.reveal_progress;
  // the typewriter generator animates this 0 → 1 over a duration.
  reveal_progress?: number;
  // Collage maskslide (Order 158.2): render the text behind a fixed clip line
  // and let the animated container be the WINDOW that rises to uncover it — a
  // true overflow mask, not a rise+fade. When set, the renderer treats the
  // resolved container as the clip window and paints the content at the layer's
  // static container (its rest position) inside it, so a container that rises
  // from below reveals the text bottom-first as it clears the line. Default
  // false = the plain single-box render. Only the maskslide motion sets this.
  clip_slide?: boolean;
}

// --- Part layer (Marionette fork work, kickoff §1) --------------------------
// A character part: an ordered set of swappable variants (rest / open / blink /
// visemes), rendered as a raw SVG <path> fill (vector) or a masked PNG. The
// container keyframe model is unchanged; the discrete swap is `active_variant_id`
// on the keyframe (generalized from a Text glyph's variant_id).
//
// Puppet-importer extension (Order — PSD/SVG puppet import). Two faithful,
// full-colour render modes were added so an imported Character-Animator puppet
// renders with its real artwork, not a single est_role tint:
//   'image'        — a full-colour raster (PSD layers flattened to a PNG that is
//                    composited at full-canvas size, so parts overlay by simply
//                    filling the rig canvas, exactly like 'vector').
//   'svg-fragment' — inline SVG markup (an Illustrator <g> placed in a
//                    full-canvas viewBox). Keeps the export's own vector colour.
// Both author their artwork in full rig-canvas coordinates (transparent
// elsewhere), so the existing "every part fills the rig canvas and overlays"
// model is unchanged — only the paint primitive differs.
//
// Mesh-deformation extension (Order 15.7). 'mesh' is the first NON-RIGID mode:
// the part's artwork is triangulated to its silhouette (a contour mesh) and
// warped per frame by the Character Animator-style solver — sticks, pin/drag
// handles, weld/hinge/free attach, Limb IK (see deform-rig.ts PartWarp). The
// solve runs at author time; playback replays the baked deform-timeline
// (deform-timeline.ts). Rendered via the WebGL (Pixi) mesh path; the other
// four modes keep the existing SVG/CSS renderer unchanged.
export type PartRenderMode = 'vector' | 'masked-png' | 'image' | 'svg-fragment' | 'mesh';

export interface PartVariant {
  id: string;                       // 'rest', 'open', 'blink-closed', or a viseme id
  label: string;
  path_d?: string;                  // render_mode = 'vector'
  source_url?: string;              // render_mode = 'masked-png' | 'image'
  svg_markup?: string;              // render_mode = 'svg-fragment' (full-canvas inner SVG)
  natural_width: number;
  natural_height: number;
  // render_mode = 'image': top-left of this (tight-cropped) variant PNG in full
  // rig-canvas coordinates. Lets each variant keep its own tight bbox (mouth
  // visemes differ in size) while still compositing into the assembled face.
  offset_x?: number;
  offset_y?: number;
}

// --- Rig-tree / swap-group extension (puppet importer) ----------------------
// Character Animator puppets are a TREE of named parts, not a flat stack. The
// importer normalizes PSD/SVG into this model:
//  - the tree is expressed via PartLayer.parent_id (the flat `layers[]` array is
//    kept for render/keyframe compatibility; parent_id overlays a hierarchy on
//    it, root parts have parent_id null/absent);
//  - a '+'-prefixed group/layer becomes an INDEPENDENT rig node (its own pivot +
//    keyable transform) — `independent: true`;
//  - pivot is the Adobe rule: an 'Origin'-named handle if present, else the
//    part's bounding-box centre, recorded in full rig-canvas coordinates;
//  - Mouth / Left Eye / Right Eye are DISCRETE SWAP CHANNELS whose named children
//    are mutually-exclusive variants (visemes / blink states). A swap channel is
//    a single PartLayer carrying those variants (the existing variants[] +
//    active_variant_id mechanism); `swap_role` classifies it and drives the
//    viseme-coverage report.

export interface PartPivot { x: number; y: number } // full rig-canvas coordinates

// --- Rigid-hinge rig (Order 129) --------------------------------------------
// A hinge joint between a part and its parent. Parts stay RIGID cutouts; the
// joint is the only articulation. Forward kinematics: a part's world transform =
//   parentWorld ∘ translate(pivot) ∘ rotate(joint.angle) ∘ translate(−pivot) ∘ localContainer
// so rotating a joint rotates this part AND everything below it in the chain.
// `angle` is keyframable (joins `container` in the keyframe values, tweened like
// rotation — see KeyframeLayerValues.joint_angle). A part with no `joint`
// (and no jointed ancestor) resolves exactly as a flat rig does, so Poopy and
// every pre-Order-129 performance are byte-unchanged.
export interface RigJoint {
  // Hinge point in PARENT-LOCAL coordinates (the same rig-canvas space the
  // parent's artwork/container live in — for a root part this is rig-canvas
  // space). The part rotates about this point.
  pivot: PartPivot;
  // Hinge angle in degrees. Default 0. Keyframable (KeyframeLayerValues.joint_angle).
  angle: number;
}

// 'mouth' / 'eye-*' are the named CharAnim swap channels (lip-sync, blink).
// 'swap' is a GENERIC swap set — any named group whose children are
// mutually-exclusive alternate views (e.g. wing poses, arm variants), detected
// by the visibility pattern (one default-visible child, the rest hidden). The
// cycle engine animates a swap set frame-by-frame; named actions trigger it.
export type SwapRole = 'mouth' | 'eye-left' | 'eye-right' | 'eye' | 'swap' | 'none';

// The standard Character Animator viseme set (mouth swap variants), in CH order.
// The importer reports which of these a puppet actually carries (lip-sync
// readiness). Ids are normalized (case-insensitive, 'W-Oo' canonical).
export const CH_VISEMES = [
  'Neutral', 'Aa', 'D', 'E', 'F', 'L', 'M', 'Oh', 'R', 'S', 'Uh', 'W-Oo', 'Smile',
] as const;
export type Viseme = typeof CH_VISEMES[number];

export interface PartLayer extends LayerBase {
  type: 'part';
  render_mode: PartRenderMode;
  variants: PartVariant[];          // ordered; length >= 1
  active_variant_id: string;        // keyframable, DISCRETE (step, no tween)
  // The rig part this layer was instantiated from (e.g. 'talk-lip-lower').
  // Optional + stable: gives conversational / programmatic surfaces (the verb
  // ops module, the MCP server) a durable handle on a part independent of its
  // uuid or display name. Absent on the blank +Add part and pre-Order-126 saves.
  rig_key?: string;
  // The part's own coordinate space (the variant paths' viewBox), used to scale
  // the raw <path> into the layer container. Poopy's parts live in a 458x436
  // rig space; each variant also records its tight-cropped natural size.
  view_box_width: number;
  view_box_height: number;

  // --- Rig-tree / swap-group fields (puppet importer). All optional so the
  // Poopy rig + every pre-import composition round-trips unchanged. ---
  // Parent rig node (another PartLayer's id). null/absent = a root part. The
  // flat layers[] array still defines paint order; this overlays the hierarchy.
  parent_id?: string | null;
  // Marked independent by a leading '+' on its source name (own pivot + keyable
  // transform). Generic auto-nested groups never set this — they're collapsed
  // into their named parent's artwork and never emitted as nodes.
  independent?: boolean;
  // Transform origin in full rig-canvas coordinates: an 'Origin' handle if the
  // source had one, else the tight bounding-box centre.
  pivot?: PartPivot;
  // Tight artwork bounding box in full rig-canvas coordinates (informational;
  // pivot defaults to its centre). Rendering still fills the full canvas.
  bounds?: { x: number; y: number; width: number; height: number };
  // Set when this part is a discrete swap channel (Mouth / Eye). Its variants[]
  // are the mutually-exclusive states; active_variant_id = the one visible on
  // import. 'none' (or absent) = an ordinary single-look part.
  swap_role?: SwapRole;
  // Rigid-hinge joint (Order 129). When present, this part hinges about
  // joint.pivot relative to its parent (forward kinematics; see RigJoint).
  // Absent/null = a flat part (independent container, no inherited rotation) —
  // the pre-Order-129 behavior, unchanged.
  joint?: RigJoint | null;
  // Character Animator warp rig (Order 15.7): contour-mesh params, pin/drag
  // handles, sticks, and the parent attach style (weld/hinge/free) the mesh
  // deformation solver consumes. Absent/null or warp.enabled === false = the
  // part stays rigid — every pre-15.7 composition round-trips unchanged.
  warp?: import('./deform-rig').PartWarp | null;
}

// --- Collage layer (collage_engine_spec v0, Order 151) ----------------------
// One collage piece, fully resolved by the Compositor's seeded composer (no
// randomness left at render time — see src/collage.ts). Container/blend/opacity
// keyframe exactly like every other layer; the piece payload carries the
// collage-specific render techniques (edges, windows, textures, interior
// motion). Additive: the Compositor's composer is the only producer, so every
// pre-Order-151 composition and Marionette performance round-trips unchanged.
export interface CollageLayer extends LayerBase {
  type: 'collage';
  collage: import('./collage').CollagePiece;
  // Order 158.4 — depth plane 0..1 for the scene camera's parallax (0 = paper,
  // 1 = nearest). Stamped by the collage composer; absent on pre-158.4
  // compositions (renderers treat absent as the shallow default).
  depth?: number;
}

export interface CameraLayer extends LayerBase {
  type: 'camera';
  // NO source_url, and that is the point: a camera layer is a SLOT, not media.
  // What fills it is the shot's own filmed footage, resolved at assembly time by
  // the Resolve assembler — the composition only says WHERE it goes and HOW BIG,
  // via the `container` every layer already has. Storing a path here would give
  // the footage a second home (the Shot's Composition Render Path is the first).
  //
  // The rect IS `container`. There is deliberately no `camera_rect` beside it:
  // one value, one owner. Split-screen and frame-inside-a-frame differ by nothing
  // except this container, which is why they are placements rather than kinds.
  //
  // The compose-time stand-in so the frame can be judged against the body the
  // real footage will carry. Compose-only: it NEVER exports (see delivery.ts).
  standin_url: string | null;
  // How the stand-in fills the camera's rect. Carried, not defaulted, because a
  // legacy `filler_background` could set it and dropping it would silently
  // re-frame those compositions. Absent = 'cover'.
  standin_fit?: Fit;
}

export type Layer =
  | TextLayer | ShapeLayer | MediaLayer | FigureLayer | AuxTextLayer | PartLayer
  | CollageLayer | CameraLayer;

export function isCameraLayer(l: Layer): l is CameraLayer {
  return l.type === 'camera';
}

/** Layers that paint and export, bottom-first. `layers[0]` is the BOTTOM — the
 *  renderer maps the array in document order, so later entries paint on top. */
export function visibleLayers(layers: Layer[]): Layer[] {
  return layers.filter(l => !l.hidden);
}

// --- Alpha defaults / migration (§14.2) -------------------------------------

// A neutral 'none' alpha record: the layer's own PNG alpha is authoritative.
// This is the back-compat default — a layer with this record behaves exactly as
// it did before Phase 9 (no manufactured alpha).
export function noneAlpha(): Alpha {
  return {
    source: 'none',
    key: null,
    segmentation: null,
    matte_url: null,
    order: 'key-then-scale',
    baked_url: null,
    baked_at: null,
    preset: null,
  };
}

// Default key params (used when a layer switches to key-color). Mid tolerance,
// no choke/despill/feather — a neutral starting point the presets override.
export function defaultKey(hex = '#00ff00'): AlphaKey {
  return { hex, tolerance: 0.3, choke: 0, despill: 0, edge_softness: 0 };
}

export function defaultSegmentation(): AlphaSegmentation {
  return { model: 'u2net', threshold: 0.5, feather: 0 };
}

// The default alpha record for a freshly created layer, by type. Per §14.2:
// Text/Figure/Shape/AuxText/Part → 'none'; Media image → 'segmentation' (stills
// default); Media video → 'key-color' (keyed live via WebGL). We seed the
// inactive param blocks too so switching source in the editor never starts null.
export function defaultAlphaForLayer(type: LayerType, mediaType?: MediaType): Alpha {
  const base = noneAlpha();
  if (type === 'media' && mediaType === 'image') {
    return { ...base, source: 'segmentation', segmentation: defaultSegmentation(), key: defaultKey() };
  }
  if (type === 'media' && mediaType === 'video') {
    return { ...base, source: 'key-color', key: defaultKey(), segmentation: null };
  }
  return base;
}

// Read a layer's alpha, filling the 'none' default when absent. All render /
// editor consumers go through this so a pre-Phase-9 layer (no `alpha` key) reads
// as 'none' and renders unchanged.
export function getLayerAlpha(layer: Pick<LayerBase, 'alpha'>): Alpha {
  return layer.alpha ?? noneAlpha();
}

// Migration: ensure a (possibly persisted-before-Phase-9) layer carries an
// `alpha` record. Old layers get source:'none' with the type-appropriate skeleton
// so their behavior is unchanged. Idempotent — a layer that already has alpha is
// returned as-is (same reference), so re-running never churns the composition.
export function ensureLayerAlpha<T extends LayerBase>(layer: T): T {
  if (layer.alpha) return layer;
  const mediaType = layer.type === 'media' ? (layer as unknown as MediaLayer).media_type : undefined;
  // Migrated layers default to 'none' regardless of type — their current PNG/
  // video behavior must not change on upgrade. (New layers created post-Phase-9
  // get the richer per-type default from defaultAlphaForLayer via the factories.)
  void mediaType;
  return { ...layer, alpha: noneAlpha() };
}
